/**
 * Środkowa kolumna: dane oferty i warunki handlowe obok siebie (układ poziomy).
 */

import React from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@mui/material';

import type { ISalesCrmOffer } from 'api/endpoints/useGetSalesCrmClientOffer';
import type { IResponse as IClientResponse } from 'api/endpoints/useGetClient';

import styles from 'theme/components/containers/TraderClientCardContainer/TraderClientData/TraderClientData.module.scss';

interface ITraderClientOfferDataProps {
  offer?: ISalesCrmOffer | null;
  client?: IClientResponse | null;
  isLoading?: boolean;
}

const formatOfferDate = (iso: string | undefined): string => {
  if (!iso) return '–';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString();
};

const formatMoney = (value: number | null | undefined): string => {
  if (value == null || Number.isNaN(value)) return '–';
  return `${value.toLocaleString('pl-PL', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })} zł`;
};

const TraderClientOfferData: React.FC<ITraderClientOfferDataProps> = ({
  offer,
  client,
  isLoading = false
}) => {
  const { t } = useTranslation();

  const wrapperClass = classnames(
    styles.wrapper,
    'StylePath-Containers-TraderClientOfferContainer-TraderClientOfferData'
  );

  if (isLoading) {
    return (
      <div className={wrapperClass}>
        <section className={styles.section}>
          <Skeleton variant="text" width={120} height={24} sx={{ mb: 1.5 }} />
          <Skeleton variant="text" width="88%" height={20} sx={{ mb: 0.5 }} />
          <Skeleton variant="text" width="72%" height={20} sx={{ mb: 0.5 }} />
          <Skeleton variant="text" width="48%" height={20} sx={{ mb: 0.5 }} />
          <Skeleton variant="text" width="56%" height={20} sx={{ mb: 0.5 }} />
          <Skeleton variant="text" width="42%" height={20} />
        </section>
        <section className={styles.section}>
          <Skeleton variant="text" width={160} height={24} sx={{ mb: 1.5 }} />
          <Skeleton variant="text" width="78%" height={20} sx={{ mb: 0.5 }} />
          <Skeleton variant="text" width="62%" height={20} sx={{ mb: 0.5 }} />
          <Skeleton variant="text" width="70%" height={20} sx={{ mb: 0.5 }} />
          <Skeleton variant="text" width="58%" height={20} />
        </section>
      </div>
    );
  }

  if (!offer) {
    return (
      <div className={wrapperClass}>
        <p className={styles.row}>
          <span className={styles.value}>{t('Nie znaleziono oferty.')}</span>
        </p>
      </div>
    );
  }

  return (
    <div className={wrapperClass}>
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>{t('Dane oferty')}</h3>
        <p className={styles.row}>
          <span className={styles.label}>{t('Nazwa oferty')}:</span>{' '}
          <span className={styles.value}>{offer.offer_name || '–'}</span>
        </p>
        <p className={styles.row}>
          <span className={styles.label}>{t('Numer oferty')}:</span>{' '}
          <span className={styles.value}>{offer.offer_number || '–'}</span>
        </p>
        <p className={styles.row}>
          <span className={styles.label}>{t('Data oferty')}:</span>{' '}
          <span className={styles.value}>{formatOfferDate(offer.offer_date)}</span>
        </p>
        <p className={styles.row}>
          <span className={styles.label}>{t('Wartość (zł)')}:</span>{' '}
          <span className={styles.value}>{offer.value || '–'}</span>
        </p>
        <p className={styles.row}>
          <span className={styles.label}>{t('Status')}:</span>{' '}
          <span className={styles.value}>{offer.status || '–'}</span>
        </p>
      </section>

      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>{t('Warunki handlowe')}</h3>
        <p className={styles.row}>
          <span className={styles.label}>{t('Sposób płatności')}:</span>{' '}
          <span className={styles.value}>{offer.payment_method || '–'}</span>
        </p>
        <p className={styles.row}>
          <span className={styles.label}>{t('Termin płatności')}:</span>{' '}
          <span className={styles.value}>–</span>
        </p>
        <p className={styles.row}>
          <span className={styles.label}>{t('Limit przyznany')}:</span>{' '}
          <span className={styles.value}>{formatMoney(client?.trade_credit_total)}</span>
        </p>
        <p className={styles.row}>
          <span className={styles.label}>{t('Limit dostępny')}:</span>{' '}
          <span className={styles.value}>{formatMoney(client?.trade_credit_free)}</span>
        </p>
      </section>
    </div>
  );
};

export default TraderClientOfferData;
